/** Low-impact action types that may be approved together (mirrors the backend). */
export const BULK_APPROVABLE_TYPES = new Set([
  'create_task',
  'update_task',
  'add_note',
  'log_activity',
  'refresh_health_score',
]);

export const WORKSPACE_GROUPS = [
  { key: 'needs_approval', label: 'Needs Approval', defaultOpen: true },
  { key: 'in_progress', label: 'In Progress', defaultOpen: true },
  { key: 'attention', label: 'Needs Attention', defaultOpen: true },
  { key: 'done', label: 'Done', defaultOpen: false },
];

export const isActionExpired = (action, now = Date.now()) => (
  action.status === 'expired'
  || (action.status === 'pending' && !!action.expires_at && new Date(action.expires_at).getTime() < now)
);

export function workspaceGroup(action, now = Date.now()) {
  if (isActionExpired(action, now)) return 'attention';
  switch (action.status) {
    case 'pending':
      return 'needs_approval';
    case 'executing':
    case 'approved':
      return 'in_progress';
    case 'failed':
    case 'execution_unknown':
      return 'attention';
    case 'executed':
      // Execution is a completed handoff, not live tracking of downstream work.
      return 'done';
    default:
      return 'done';
  }
}

/**
 * Group actions into workspace sections. `orderedIds` lists ids newest reply
 * first with Caleb's proposal order kept inside each reply; actions not in it
 * (older replies known only from the server) follow in their given order.
 */
export function groupWorkspaceActions(actions, now = Date.now(), orderedIds = []) {
  const rank = new Map(orderedIds.map((id, index) => [id, index]));
  const known = actions
    .filter((action) => rank.has(action.id))
    .sort((a, b) => rank.get(a.id) - rank.get(b.id));
  const rest = actions.filter((action) => !rank.has(action.id));
  const groups = Object.fromEntries(WORKSPACE_GROUPS.map(({ key }) => [key, []]));
  [...known, ...rest].forEach((action) => groups[workspaceGroup(action, now)].push(action));
  return groups;
}

/** Translate an approve API result into the local action patch. */
export function approvalResultPatch(result = {}) {
  if (typeof result.success !== 'boolean') {
    throw new Error('The execution outcome is missing. Refresh before trying again.');
  }
  if (result.success) {
    return { status: 'executed', result: result.result ?? null, error: null };
  }
  return {
    status: result.status ?? (
      result.error_code === 'EXECUTION_OUTCOME_UNKNOWN' ? 'execution_unknown' : 'failed'
    ),
    result: result.result ?? null,
    error: result.error ?? null,
  };
}

const GROUP_TONE = {
  needs_approval: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  in_progress: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  attention: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  done: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
};

const STATUS_LABEL = {
  pending: 'Needs Approval',
  executing: 'Executing',
  approved: 'Approved',
  failed: 'Failed',
  execution_unknown: 'Needs Review',
  rejected: 'Rejected',
};

/** Short status label and tone for chips and section rows. */
export function workspaceStatus(action, now = Date.now()) {
  const group = workspaceGroup(action, now);
  let label = STATUS_LABEL[action.status] ?? 'Done';
  if (isActionExpired(action, now)) label = 'Expired';
  else if (action.status === 'executed') {
    const state = action.result?.execution_state;
    if (action.result?.deferred || state === 'deferred') label = 'Queued';
    else if (state === 'scheduled') label = 'Scheduled';
    else if (state === 'awaiting_approval') label = 'Handed Off';
    else label = 'Done';
  }
  const tone = action.status === 'rejected'
    ? 'bg-muted text-muted-foreground'
    : GROUP_TONE[group];
  return { group, label, tone };
}

/** What the action is about: automation target, task title, email subject or name. */
export function actionTarget(action) {
  const params = action.parameters || {};
  return action.target_name || params.title || params.subject || params.name || '';
}

/** Account(s) the action applies to, for display next to the target. */
export function actionAccountLabel(action) {
  const names = action.account_names || [];
  if (names.length > 1) return `${names.length} accounts`;
  return action.account_name || names[0]
    || (action.account_id ? `Account ${action.account_id}` : '');
}
